import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { apiFetch } from '../utils/apiUtils';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const columnHelper = createColumnHelper();

// ─── Bus type colors & labels ────────────────────────────────────────────────
const BUS_COLORS = {
  male:   { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', label: 'Men Only', badge: 'bg-blue-200 text-blue-800' },
  female: { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-800', label: 'Women Only', badge: 'bg-pink-200 text-pink-800' },
  mixed:  { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', label: 'Mixed', badge: 'bg-purple-200 text-purple-800' },
};

function BusAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [flightId, setFlightId] = useState('');
  const [flights, setFlights] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [busFilter, setBusFilter] = useState('');
  const [sorting, setSorting] = useState([{ id: 'bus_number', desc: false }]);
  const [showAutoAssignConfirm, setShowAutoAssignConfirm] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'bus-manager'
  const queryClient = useQueryClient();

  const accessCode = searchParams.get('access_code');
  const flightIdFromUrl = searchParams.get('flight');

  // ─── Auth ──────────────────────────────────────────────────────────────────
  const authQuery = useQuery({
    queryKey: ['busAuth', accessCode],
    queryFn: () => apiFetch(`${API_URL}/bus-admin/verify?access_code=${accessCode}`),
    enabled: !!accessCode,
    retry: false,
  });

  useEffect(() => {
    if (authQuery.isSuccess && authQuery.data?.authenticated) {
      setAuthenticated(true);
      setUserRole(authQuery.data.user?.role || 'bus-manager');
      const flightsData = authQuery.data.flights || {};
      setFlights(flightsData);
      // Default to URL param, then first open flight, then first flight
      if (!flightId) {
        const urlFlight = flightIdFromUrl && flightsData[flightIdFromUrl] ? flightIdFromUrl : null;
        const openFlight = Object.entries(flightsData).find(([, f]) => f.isOpen);
        setFlightId(urlFlight || (openFlight ? openFlight[0] : Object.keys(flightsData)[0] || ''));
      }
    }
  }, [authQuery.isSuccess, authQuery.data]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (codeInput.trim()) {
      setSearchParams({ access_code: codeInput.trim() });
    }
  };

  // ─── Passengers query ──────────────────────────────────────────────────────
  const passengersQuery = useQuery({
    queryKey: ['busPassengers', flightId, accessCode],
    queryFn: () => apiFetch(`${API_URL}/bus-admin/passengers?access_code=${accessCode}&flight_id=${flightId}`),
    enabled: authenticated && !!flightId,
    refetchInterval: 15000, // Refresh every 15s for live boarding
  });

  const passengers = passengersQuery.data?.passengers || [];

  // ─── Mutations ─────────────────────────────────────────────────────────────
  const autoAssignMutation = useMutation({
    mutationFn: (params) => apiFetch(`${API_URL}/bus-admin/auto-assign`, {
      method: 'POST',
      body: JSON.stringify({ access_code: accessCode, flight_id: flightId, ...params }),
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['busPassengers']);
      toast.success(`Assigned ${data.assignments} passengers to ${data.summary?.length || 0} buses`);
      setShowAutoAssignConfirm(false);
    },
    onError: (err) => toast.error(`Auto-assign failed: ${err.message}`),
  });

  const assignMutation = useMutation({
    mutationFn: ({ registrationId, bus_number, bus_type }) =>
      apiFetch(`${API_URL}/bus-admin/assign`, {
        method: 'POST',
        body: JSON.stringify({ access_code: accessCode, registrationId, bus_number, bus_type }),
      }),
    onSuccess: () => queryClient.invalidateQueries(['busPassengers']),
    onError: (err) => {
      toast.error(`Assign failed: ${err.message}`);
      queryClient.invalidateQueries(['busPassengers']); // rollback optimistic update
    },
  });

  const checkinMutation = useMutation({
    mutationFn: ({ registrationId, field, value }) =>
      apiFetch(`${API_URL}/bus-admin/checkin`, {
        method: 'POST',
        body: JSON.stringify({ access_code: accessCode, registrationId, field, value }),
      }),
    onError: (err) => {
      toast.error(`Check-in update failed: ${err.message}`);
      queryClient.invalidateQueries(['busPassengers']); // rollback optimistic update
    },
  });

  // ─── Bus summary ──────────────────────────────────────────────────────────
  const busSummary = useMemo(() => {
    const summary = {};
    for (const p of passengers) {
      if (p.bus_number == null) continue;
      if (!summary[p.bus_number]) {
        summary[p.bus_number] = { busNumber: p.bus_number, busType: p.bus_type || 'mixed', count: 0, boarded: 0, confirmed: 0 };
      }
      summary[p.bus_number].count++;
      if (p.bus_boarded) summary[p.bus_number].boarded++;
      if (p.bus_confirmed) summary[p.bus_number].confirmed++;
    }
    const unassigned = passengers.filter(p => p.bus_number == null).length;
    const acknowledged = passengers.filter(p => !!p.schedule_ack_at).length;
    return { buses: Object.values(summary).sort((a, b) => a.busNumber - b.busNumber), unassigned, total: passengers.length, acknowledged };
  }, [passengers]);

  // ─── Optimistic checkin handler ────────────────────────────────────────────
  const handleCheckin = (registrationId, field, currentValue) => {
    const newValue = !currentValue;
    // Optimistic update
    queryClient.setQueryData(['busPassengers', flightId, accessCode], (old) => {
      if (!old?.passengers) return old;
      return {
        ...old,
        passengers: old.passengers.map(p => p.id === registrationId ? { ...p, [field]: newValue } : p),
      };
    });
    checkinMutation.mutate({ registrationId, field, value: newValue });
  };

  // ─── Optimistic schedule ack toggle ───────────────────────────────────────
  const handleScheduleAckToggle = (registrationId, currentValue) => {
    const isAcked = !!currentValue;
    const newValue = !isAcked;
    // Optimistic update: set to a truthy string or null
    queryClient.setQueryData(['busPassengers', flightId, accessCode], (old) => {
      if (!old?.passengers) return old;
      return {
        ...old,
        passengers: old.passengers.map(p =>
          p.id === registrationId
            ? { ...p, schedule_ack_at: newValue ? new Date().toISOString() : null }
            : p
        ),
      };
    });
    checkinMutation.mutate({ registrationId, field: 'schedule_ack_at', value: newValue });
  };

  // ─── Bus reassign handler ─────────────────────────────────────────────────
  const handleBusChange = (registrationId, newBusNumber) => {
    const busNum = newBusNumber === '' ? null : parseInt(newBusNumber);
    // Derive bus_type from existing bus occupants
    let busType = null;
    if (busNum != null) {
      const existing = passengers.find(p => p.bus_number === busNum);
      busType = existing?.bus_type || 'mixed';
    }
    // Optimistic update
    queryClient.setQueryData(['busPassengers', flightId, accessCode], (old) => {
      if (!old?.passengers) return old;
      return {
        ...old,
        passengers: old.passengers.map(p => p.id === registrationId ? { ...p, bus_number: busNum, bus_type: busType } : p),
      };
    });
    assignMutation.mutate({ registrationId, bus_number: busNum, bus_type: busType });
  };

  // ─── Table columns ────────────────────────────────────────────────────────
  const maxBusNumber = Math.max(5, ...passengers.map(p => p.bus_number || 0));
  const busOptions = Array.from({ length: maxBusNumber }, (_, i) => i + 1);

  const columns = useMemo(() => [
    columnHelper.accessor('bus_number', {
      header: 'Bus',
      size: 70,
      cell: ({ row }) => (
        <select
          value={row.original.bus_number ?? ''}
          onChange={(e) => handleBusChange(row.original.id, e.target.value)}
          className="w-14 py-1 px-1 border rounded text-sm bg-white cursor-pointer"
        >
          <option value="">—</option>
          {busOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      ),
    }),
    columnHelper.accessor(row => `${row.last_name}, ${row.first_name}`, {
      id: 'name',
      header: 'Name',
      size: 180,
      cell: ({ getValue, row }) => (
        <div>
          <span className="font-medium">{getValue()}</span>
          {row.original.bus_type === 'mixed' && row.original.bus_number != null && (
            <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1 rounded">family</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('gender', {
      header: 'Gen',
      size: 50,
      cell: ({ getValue }) => {
        const g = (getValue() || '').toLowerCase();
        return (
          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
            g === 'male' ? 'bg-blue-100 text-blue-700' : g === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {g === 'male' ? 'M' : g === 'female' ? 'F' : '?'}
          </span>
        );
      },
    }),
    columnHelper.accessor('registration_code', {
      header: 'Code',
      size: 90,
      cell: ({ getValue }) => <span className="text-xs font-mono">{getValue() || '—'}</span>,
    }),
    columnHelper.accessor('school', {
      header: 'School',
      size: 140,
      cell: ({ getValue }) => <span className="text-sm truncate block max-w-[140px]" title={getValue()}>{getValue()}</span>,
    }),
    columnHelper.accessor('route', {
      header: 'Route',
      size: 60,
      cell: ({ getValue }) => {
        const r = getValue() || '';
        const isNYC = r.includes('newyork');
        return (
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isNYC ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isNYC ? 'NYC' : 'MLN'}
          </span>
        );
      },
    }),
    columnHelper.accessor('phone', {
      header: 'Phone',
      size: 130,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <a href={`tel:${v}`} className="text-blue-600 hover:underline text-sm whitespace-nowrap">{v}</a> : <span className="text-gray-400">—</span>;
      },
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      size: 180,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <a href={`mailto:${v}`} className="text-blue-600 hover:underline text-sm truncate block max-w-[180px]" title={v}>{v}</a> : <span className="text-gray-400">—</span>;
      },
    }),
    columnHelper.accessor('bus_boarded', {
      header: 'B',
      size: 44,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={!!row.original.bus_boarded}
          onChange={() => handleCheckin(row.original.id, 'bus_boarded', row.original.bus_boarded)}
          className="w-5 h-5 cursor-pointer accent-green-600"
        />
      ),
    }),
    columnHelper.accessor('bus_confirmed', {
      header: 'C',
      size: 44,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={!!row.original.bus_confirmed}
          onChange={() => handleCheckin(row.original.id, 'bus_confirmed', row.original.bus_confirmed)}
          className="w-5 h-5 cursor-pointer accent-blue-600"
        />
      ),
    }),
    columnHelper.accessor('schedule_ack_at', {
      header: 'Ack',
      size: 44,
      cell: ({ row }) => {
        const acked = !!row.original.schedule_ack_at;
        return (
          <button
            onClick={() => handleScheduleAckToggle(row.original.id, row.original.schedule_ack_at)}
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            title={acked ? `Acknowledged${typeof row.original.schedule_ack_at === 'string' ? ` at ${new Date(row.original.schedule_ack_at).toLocaleString()}` : ''}` : 'Not acknowledged — click to mark'}
          >
            {acked
              ? <span className="text-green-600 text-lg font-bold">&#10003;</span>
              : <span className="text-red-500 text-lg font-bold">&#10007;</span>}
          </button>
        );
      },
    }),
  ], [passengers, busOptions]);

  // ─── Table filtering ───────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = passengers;
    if (busFilter === 'unassigned') {
      data = data.filter(p => p.bus_number == null);
    } else if (busFilter) {
      data = data.filter(p => p.bus_number === parseInt(busFilter));
    }
    return data;
  }, [passengers, busFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
  });

  // ─── Login screen ─────────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm">
          <h1 className="text-2xl font-bold text-center mb-2">Bus Management</h1>
          <p className="text-gray-500 text-center mb-6 text-sm">Enter access code to continue</p>
          {(authQuery.isError || (authQuery.isSuccess && !authQuery.data?.authenticated)) && (
            <p className="text-red-600 text-sm text-center mb-4">Invalid access code</p>
          )}
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="Access code"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 text-lg"
              autoFocus
            />
            <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Main UI ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-bold whitespace-nowrap">Bus Management</h1>
          <select
            value={flightId}
            onChange={(e) => { setFlightId(e.target.value); setBusFilter(''); setSearchParams(p => { const n = new URLSearchParams(p); n.set('flight', e.target.value); return n; }); }}
            className="px-3 py-2 border rounded-lg text-sm font-medium bg-white"
          >
            {Object.entries(flights).map(([id, f]) => (
              <option key={id} value={id}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Bus summary cards */}
        <div className="flex gap-3 overflow-x-auto pb-3 mb-4">
          {busSummary.buses.map(bus => {
            const colors = BUS_COLORS[bus.busType] || BUS_COLORS.mixed;
            return (
              <button
                key={bus.busNumber}
                onClick={() => setBusFilter(busFilter === String(bus.busNumber) ? '' : String(bus.busNumber))}
                className={`flex-shrink-0 rounded-lg border-2 p-3 min-w-[120px] text-left transition-all ${colors.border} ${colors.bg} ${
                  busFilter === String(bus.busNumber) ? 'ring-2 ring-offset-1 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-bold text-lg ${colors.text}`}>Bus {bus.busNumber}</span>
                </div>
                <div className={`text-xs font-medium ${colors.text} opacity-75 mb-2`}>{colors.label}</div>
                <div className="text-2xl font-bold text-gray-800">{bus.count}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Boarded: {bus.boarded}/{bus.count} &middot; Confirmed: {bus.confirmed}/{bus.count}
                </div>
              </button>
            );
          })}
          {busSummary.unassigned > 0 && (
            <button
              onClick={() => setBusFilter(busFilter === 'unassigned' ? '' : 'unassigned')}
              className={`flex-shrink-0 rounded-lg border-2 border-gray-300 bg-gray-50 p-3 min-w-[120px] text-left transition-all ${
                busFilter === 'unassigned' ? 'ring-2 ring-offset-1 ring-blue-500' : ''
              }`}
            >
              <div className="font-bold text-lg text-gray-600">Unassigned</div>
              <div className="text-2xl font-bold text-gray-800 mt-2">{busSummary.unassigned}</div>
            </button>
          )}
        </div>

        {/* Totals row */}
        <div className="text-sm text-gray-600 mb-3">
          Total: {busSummary.total} passengers &middot; Assigned: {busSummary.total - busSummary.unassigned} &middot; Unassigned: {busSummary.unassigned}
          {busSummary.buses.length > 0 && (
            <span> &middot; Boarded: {busSummary.buses.reduce((s, b) => s + b.boarded, 0)}/{busSummary.total} &middot; Confirmed: {busSummary.buses.reduce((s, b) => s + b.confirmed, 0)}/{busSummary.total}</span>
          )}
          <span> &middot; Acked: {busSummary.acknowledged}/{busSummary.total}</span>
        </div>

        {/* Actions bar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {userRole === 'admin' && (
            <button
              onClick={() => setShowAutoAssignConfirm(true)}
              disabled={autoAssignMutation.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
            >
              {autoAssignMutation.isLoading ? 'Assigning...' : 'Auto-Assign All'}
            </button>
          )}
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search by name, school, code..."
            className="px-3 py-2 border rounded-lg text-sm flex-1 min-w-[200px]"
          />
          {(busFilter || globalFilter) && (
            <button
              onClick={() => { setBusFilter(''); setGlobalFilter(''); }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Auto-assign confirmation modal */}
        {showAutoAssignConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-lg font-bold mb-2">Auto-Assign All Passengers?</h3>
              <p className="text-gray-600 text-sm mb-4">
                This will overwrite all current bus assignments and reset all boarding/confirmation checkboxes.
                Passengers will be separated by gender (tznius) and grouped by school where possible.
                Families/couples will be kept together.
              </p>
              <p className="text-amber-600 text-sm font-medium mb-4">
                This is a best-effort assignment — you can manually adjust afterwards.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowAutoAssignConfirm(false)}
                  className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => autoAssignMutation.mutate({})}
                  disabled={autoAssignMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {autoAssignMutation.isLoading ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Passengers table */}
        {passengersQuery.isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading passengers...</div>
        ) : passengersQuery.isError ? (
          <div className="text-center py-12 text-red-500">Error loading passengers: {passengersQuery.error.message}</div>
        ) : (
          <div className="bg-white rounded-lg shadow border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-3 py-2 text-left font-semibold text-gray-700 cursor-pointer select-none whitespace-nowrap"
                        style={{ width: header.getSize() }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' ? ' ↑' : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, i) => {
                  const busType = row.original.bus_type;
                  const colors = BUS_COLORS[busType];
                  return (
                    <tr
                      key={row.id}
                      className={`border-b last:border-0 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-2" style={{ width: cell.column.getSize() }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-500">
                      {passengers.length === 0 ? 'No paid passengers for this flight' : 'No passengers match your filters'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-gray-400 mt-4 text-center">
          B = Boarded (initial check) &middot; C = Confirmed (final count) &middot; Ack = Schedule Acknowledged &middot; Auto-refreshes every 15s
        </div>
      </div>
    </div>
  );
}

export default BusAdmin;
